import React, { useEffect, useState, useCallback } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import PartySelectSection from '../../../../documents/components/PartySelectSection'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Box, ToggleButton, ToggleButtonGroup, Card, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import ItemsTableSection from '../../../../documents/components/ItemsTableSection';
import { GstType, InvoiceFormData, TaxBreakdownItem } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams, useLocation } from 'react-router-dom';

const CreateSalesInvoice = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultInvoiceNumber = `INV${financialYear}-1`
  const navigate = useNavigate();
  const location = useLocation();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [, setTotalTax] = useState(0);
  const [, setTotalDiscount] = useState(0);
  const [, setItems] = useState<any[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdownItem[]>([]);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(defaultInvoiceNumber);
  const [isLoadingInvoiceNumber, setIsLoadingInvoiceNumber] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingQuotation, setLoadingQuotation] = useState(false);

  const queryGstType = searchParams.get('gstType');

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<InvoiceFormData>({
    defaultValues: {
      invoiceType: 'INVOICE',
      gstType: getDefaultGstType(), // Use the function to get default
      party: '',
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      paymentTerms: '',
      items: [],
      charges: [],
      discount: undefined,
      notes: '',
      terms: '',
      roundOff: false,
      totalAmount: 0,
      taxableAmount: 0,
      subtotal: 0,
      receivedAmount: 0,
      balanceAmount: 0,
      taxBreakdown: [],
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch gstType value
  const gstType = watch('gstType', getDefaultGstType());
  const invoiceType = watch('invoiceType', 'INVOICE');

  // Function to fetch next invoice number from API
  const fetchNextInvoiceNumber = useCallback(async () => {
    if (isEditMode) return; // Don't fetch for edit mode

    setIsLoadingInvoiceNumber(true);
    try {
      const res = await request({
        url: 'invoice/get-next-invoice-number',
        method: "get",
        params: { invoiceType: invoiceType, gstType: gstType }
      });

      if (res.success && res.invoiceNumber) {
        const newInvoiceNumber = res.invoiceNumber;
        setNextInvoiceNumber(newInvoiceNumber);
        setValue('invoiceNumber', newInvoiceNumber);
      }
    } catch (error) {
      console.error("Error fetching next invoice number:", error);
      // Fallback: Use default
      setNextInvoiceNumber(`INV${financialYear}-1`);
      setValue('invoiceNumber', `INV${financialYear}-1`);
    } finally {
      setIsLoadingInvoiceNumber(false);
    }
  }, [financialYear, gstType, invoiceType, isEditMode, setValue]);

  // Function to fetch existing invoice data for edit mode
  const fetchInvoiceData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `invoice/get-single-sales-invoice/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const invoiceData = res.data;

        // Format dates to YYYY-MM-DD
        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Get party ID - handle both object and string
        let partyId = '';
        if (typeof invoiceData.party === 'object' && invoiceData.party !== null) {
          partyId = invoiceData.party._id || '';
        } else if (typeof invoiceData.party === 'string') {
          partyId = invoiceData.party;
        }

        // Prepare form data
        const formData = {
          invoiceType: invoiceData.invoiceType || 'INVOICE',
          gstType: invoiceData.gstType || 'GST',
          paymentType: invoiceData.paymentType,
          party: partyId,
          invoiceNumber: invoiceData.invoiceNumber || '',
          invoiceDate: formatDate(invoiceData.invoiceDate),
          date: formatDate(invoiceData.date),
          dueDate: formatDate(invoiceData.dueDate),
          paymentTerms: invoiceData.paymentTerms || '',
          items: invoiceData.items || [],
          charges: invoiceData.charges || [],
          discount: invoiceData.discount || undefined,
          notes: invoiceData.notes || '',
          terms: invoiceData.terms || '',
          roundOff: invoiceData.roundOff || false,
          totalAmount: invoiceData.totalAmount || 0,
          taxableAmount: invoiceData.taxableAmount || 0,
          subtotal: invoiceData.subtotal || 0,
          receivedAmount: invoiceData.receivedAmount || 0,
          balanceAmount: invoiceData.balanceAmount || 0,
          taxBreakdown: invoiceData.taxBreakdown || [],
        };

        // Reset form with fetched data
        reset(formData);

        // Update state values
        setTaxableAmount(invoiceData.taxableAmount || 0);
        setTotalAmount(invoiceData.totalAmount || 0);
        setTotalTax(0); // You might need to calculate this from items
        setTotalDiscount(0); // You might need to calculate this from items
        setItems(invoiceData.items || []);
        setTaxBreakdown(invoiceData.taxBreakdown || []);

      } else {
        toast.error(res.message || "Failed to load invoice data");
      }
    } catch (error: any) {
      console.error("Error fetching invoice data:", error);
      toast.error(error?.message || "Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Function to update all items tax to "none" when NON-GST is selected
  const updateItemsTaxToNone = useCallback(() => {
    const currentItems = getValues('items') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: any) => ({
      ...item,
      tax: {
        type: 'none',
        rate: 0
      },
      amount: ((item.quantity || 1) * (item.price || 0)) - (item.discount?.amount || 0)
    }));

    setItems(updatedItems);
    setValue('items', updatedItems);
    setTaxBreakdown([]);
    setValue('taxBreakdown', []);
  }, [getValues, setValue]);

  // Effect to handle query parameter for GST type (only for create mode)
  useEffect(() => {
    if (!isEditMode && queryGstType === 'NON-GST') {
      setValue('gstType', 'NON-GST');
      // Update items tax to none for NON-GST
      updateItemsTaxToNone();
    }
  }, [isEditMode, queryGstType, setValue, updateItemsTaxToNone]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchInvoiceData();
    } else {
      fetchNextInvoiceNumber();
    }
  }, [isEditMode, fetchInvoiceData, fetchNextInvoiceNumber]);

  // Update next invoice number when invoice type changes (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextInvoiceNumber();
    }
  }, [invoiceType, isEditMode, fetchNextInvoiceNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  // Handle GST type change
  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
      if (newGstType === 'NON-GST') {
        updateItemsTaxToNone();
      }
    }
  };

  // Effect to handle GST type changes
  useEffect(() => {
    if (gstType === 'NON-GST') {
      updateItemsTaxToNone();
    }
  }, [gstType, updateItemsTaxToNone]);

  const onSubmit = async (data: InvoiceFormData) => {

    try {
      const state = location.state as { source?: string; quotationId?: string };
      // Prepare the payload
      const payload = {
        ...data,
        party: data.party,
        invoiceNumber: isEditMode ? data.invoiceNumber : (data.invoiceNumber || undefined),
        ...(state?.source === 'quotation' && state?.quotationId && {
          convertedFromQuotationId: state.quotationId
        })
      };

      const apiUrl = isEditMode
        ? `invoice/update-sales-invoice/${id}`
        : 'invoice/create-sales-invoice';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        const successMessage = isEditMode
          ? "Invoice Updated Successfully"
          : "Invoice Created Successfully";
        toast.success(res.message || successMessage);

        // Navigate to invoices list
        navigate('/accounts/sales/salesInvoice');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error submitting invoice:", error);
      toast.error(error || 'Something want wrong');
    }
  }

  useEffect(() => {
    const fetchQuotationData = async () => {
      const state = location.state as { source?: string, quotationId?: string };
      if (state?.source === 'quotation' && state?.quotationId && !isEditMode) {
        setLoadingQuotation(true);
        try {
          const res = await request({
            url: `quotation/get-single-quotation/${state.quotationId}`,
            method: 'get'
          })
          if (res.success && res.data) {
            const quotationData = res.data;
            const formatDate = (dateString: string | Date) => {
              if (!dateString) return new Date().toISOString().split('T')[0];
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            };

            let partyId = '';
            if (typeof quotationData.party === 'object' && quotationData.party !== null) {
              partyId = quotationData.party._id || '';
            } else if (typeof quotationData.party === 'string') {
              partyId = quotationData.party;
            };

            const transformedItems = quotationData.items?.map((item: any, index: any) => ({
              id: item.id,
              srNo: index + 1,
              productId: item.productId,
              itemName: item.product?.itemName || item.itemName || '',
              description: item?.description || '',
              quantity: item.quantity || 1,
              price: item.price || 0,
              amount: item.amount || 0,
              discount: item.discount || { type: 'none', amount: 0 },
              tax: item.tax || { type: 'none', rate: 0 },
              hsnNo: item.hsnNo || '',
            })) || [];

            const formData: InvoiceFormData = {
              invoiceType: 'INVOICE',
              gstType: quotationData.gstType,
              party: partyId,
              invoiceNumber: '', // Will be auto-generated
              invoiceDate: formatDate(new Date()),
              date: formatDate(new Date()),
              dueDate: formatDate(quotationData.dueDate || new Date()),
              paymentTerms: quotationData.paymentTerms || '',
              items: transformedItems,
              charges: quotationData.charges || [],
              discount: quotationData.discount || undefined,
              notes: `Converted from Quotation: ${quotationData.quotationNumber}\n${quotationData.notes || ''}`,
              terms: quotationData.terms || '',
              roundOff: quotationData.roundOff || false,
              totalAmount: quotationData.totalAmount || 0,
              taxableAmount: quotationData.taxableAmount || 0,
              subtotal: quotationData.subtotal || 0,
              receivedAmount: 0,
              balanceAmount: quotationData.totalAmount || 0,
              taxBreakdown: quotationData.taxBreakdown || [],
            };

            reset(formData);

            // Update state values
            setTaxableAmount(quotationData.taxableAmount || 0);
            setTotalAmount(quotationData.totalAmount || 0);
            setItems(transformedItems);
            setTaxBreakdown(quotationData.taxBreakdown || []);

            toast.success(`Quotation ${quotationData.quotationNumber} loaded successfully`);
          }
        } catch (error: any) {
          toast.error(error || "Failed to load quotation data")
        } finally {
          setLoadingQuotation(false);
        }
      }
    };
    fetchQuotationData();
  }, [isEditMode, reset, location.state])

  if (loading || loadingQuotation) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading invoice data...</Typography>
      </Box>
    )
  }

  const handleCancel = () => {
    navigate('/accounts/sales/salesInvoice');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading invoice data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Edit Sales Invoice' : 'Create Sales Invoice'}
        showBackButton
        showCancelButton={true}
        showSaveButton={true}
        onCancel={handleCancel}
        onSave={handleSubmit(onSubmit)}
        saveButtonText={isEditMode ? 'Update Invoice' : 'Create Invoice'}
        saveDisabled={isSubmitting}
      />

      {/* GST Type Selection Section */}
      {!gstEnabled && (
        <Card sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Invoice Type:
            </Typography>

            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              aria-label="GST Type"
              size="small"
              color="primary"
              disabled={isEditMode} // Disable in edit mode
            >
              <ToggleButton value="GST" aria-label="GST">
                GST Invoice
              </ToggleButton>
              <ToggleButton value="NON-GST" aria-label="NON-GST">
                Non-GST Invoice
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color={gstType === 'GST' ? "success.main" : "info.main"}>
              {gstType === 'GST'
                ? 'Includes GST tax calculations'
                : 'No GST tax calculations'}
            </Typography>
          </Box>
          {isEditMode && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
              GST type cannot be changed for existing invoices.
            </Alert>
          )}
        </Card>
      )}

      {/* Form Provider */}
      <FormProvider {...methods}>
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={'flex'} gap={1} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, height: 'auto' }}>
              <PartySelectSection
                request={requestWrapper}
                apiParams={{ status: 'active', limit: 10000 }}
                billToLabel="Bill To"
                formName="party"
                onAddNewParty={() => navigate('/parties/addParty')}
                compact={true}
                sendPartyIdOnly={true}
              // Pass the current party value for edit mode
              />

              {errors.party && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a party
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <InvoiceDatesSection
                invoiceNumberLabel="Sales Invoice No"
                invoiceDateLabel="Sales Invoice Date"
                paymentTermsLabel="Payment Terms"
                dueDateLabel="Due Date"
                formFieldNames={{
                  invoiceNumber: 'invoiceNumber',
                  invoiceDate: 'invoiceDate',
                  paymentTerms: 'paymentTerms',
                  dueDate: 'dueDate'
                }}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextInvoiceNumber}
                showPaymentTerms={true}
                showDueDate={true}
                compact={true}
                disabled={isLoadingInvoiceNumber || isEditMode} // Disable invoice number in edit mode
                onInvoiceDateChange={() => { }}
                onPaymentTermsChange={() => { }}
                onDueDateChange={() => { }}
                paymentTermsOptions={[
                  { value: 'immediate', label: 'Immediate', days: 0 },
                  { value: 'net7', label: 'Net 7 Days', days: 7 },
                  { value: 'net15', label: 'Net 15 Days', days: 15 },
                  { value: 'net30', label: 'Net 30 Days', days: 30 },
                  { value: 'net45', label: 'Net 45 Days', days: 45 },
                  { value: 'net60', label: 'Net 60 Days', days: 60 },
                ]}
              />
              {isLoadingInvoiceNumber && !isEditMode && (
                <Typography variant="caption" color="text.secondary">
                  Loading next invoice number...
                </Typography>
              )}
              {nextInvoiceNumber && !isEditMode && (
                <Typography variant="caption" color="primary">
                  Next available number: {nextInvoiceNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Items Table Section */}
          <Box>
            <ItemsTableSection
              formFieldName="items"
              onItemsChange={(updatedItems) => {
                setItems(updatedItems);
                setValue('items', updatedItems);
              }}
              onSubtotalChange={() => { }}
              onTotalDiscountChange={(discount) => {
                setTotalDiscount(discount);
              }}
              onTotalTaxChange={(tax) => {
                setTotalTax(tax);
              }}
              onTaxableAmountChange={(taxable) => {
                setTaxableAmount(taxable);
                setValue('taxableAmount', taxable);
              }}
              onTotalAmountChange={(total) => {
                setTotalAmount(total);
                setValue('totalAmount', total);
              }}
              onTaxBreakdownChange={(breakdown) => {
                if (gstType === 'GST') {
                  setTaxBreakdown(breakdown);
                  setValue('taxBreakdown', breakdown);
                }
              }}
              showHsnNo={gstType === 'GST'}
              showDiscount={true}
              showTax={gstType === 'GST'}
              disabled={false}
              compact={true}
              maxItems={100}
            // Items will be automatically populated from the form
            />
          </Box>

          <Box display={'flex'} gap={1} sx={{ alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                termsLabel="Terms & Conditions"
                addNoteButtonText="Add Note"
                addTermsButtonText="Add Terms"
                formFieldNames={{
                  notes: 'notes',
                  terms: 'terms'
                }}
                showNotes={true}
                showTerms={true}
                allowMultipleNotes={false}
                allowMultipleTerms={false}
                compact={true}
                maxNotesLength={500}
                maxTermsLength={1000}
                onNotesChange={() => { }}
                onTermsChange={() => { }}
                notesPlaceholder="Add any special instructions, delivery notes, or additional information..."
                termsPlaceholder="Add payment terms, return policies, warranties, or other conditions..."
                defaultExpanded={!!getValues('notes') || !!getValues('terms')}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AmountSummarySection
                taxableAmount={taxableAmount}
                totalAmount={totalAmount}
                taxBreakdown={gstType === 'GST' ? taxBreakdown : []}
                formFieldNames={{
                  charges: 'charges',
                  discount: 'discount',
                  roundOff: 'roundOff',
                  totalAmount: 'totalAmount',
                  taxableAmount: 'taxableAmount',
                  subtotal: 'subtotal',
                  paymentType: 'paymentType',
                  receivedAmount: 'receivedAmount',
                  taxBreakdown: 'taxBreakdown',
                  balanceAmount: 'balanceAmount'
                }}
                compact={true}
                taxRate={gstType === 'GST' ? 18 : 0}
                showTaxableAmount={gstType === 'GST'}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreateSalesInvoice;